import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#F8F5EF]">
        <div className="w-full max-w-md animate-fade-in-up">
          <div className="mb-10">
            <img src="/assets/stratum_logo.png" alt="Stratum" className="h-12 mb-6" />
            <h1 className="font-display text-3xl font-semibold text-[#0B1020] mb-2">Welcome back</h1>
            <p className="text-[#6B7280]">Sign in to your asset register</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-3 rounded-lg bg-[#991B1B]/10 text-[#991B1B] text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-[#1A1A1A] mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="stratum-input w-full"
                placeholder="admin@stratum.app"
                required
                data-testid="login-email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1A1A1A] mb-1.5">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="stratum-input w-full"
                placeholder="Enter your password"
                required
                data-testid="login-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="stratum-btn-primary w-full py-3 disabled:opacity-50"
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-[#6B7280]">
            Don't have an account?{" "}
            <Link to="/register" className="text-[#C8A961] hover:underline font-medium" data-testid="register-link">
              Create one
            </Link>
          </p>

          <div className="mt-10 text-center text-xs text-[#6B7280]/50">
            Powered by Stratum - The Intelligent Asset Register
          </div>
        </div>
      </div>

      {/* Right panel - Hero */}
      <div className="hidden lg:flex w-1/2 bg-[#0B1020] items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1020] via-[#0B1020] to-[#1A233A]" />
        <div className="relative z-10 text-center max-w-lg">
          <div className="mb-8 flex justify-center">
            <img src="/assets/stratum_logomark.png" alt="Stratum" className="w-24 h-24 rounded-2xl" />
          </div>
          <h2 className="font-display text-4xl font-semibold text-white mb-4">
            The Intelligent<br />Asset Register
          </h2>
          <p className="text-white/50 text-lg leading-relaxed">
            IFRS-compliant, AI-driven, audit-ready. Every layer of your asset value — cost, depreciation, carrying amount — in one place.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-2xl font-display font-semibold text-[#C8A961] tabular-nums">14</div>
              <div className="text-xs text-white/40 mt-1">IFRS Standards</div>
            </div>
            <div>
              <div className="text-2xl font-display font-semibold text-[#C8A961] tabular-nums">100%</div>
              <div className="text-xs text-white/40 mt-1">Audit-Ready</div>
            </div>
            <div>
              <div className="text-2xl font-display font-semibold text-[#C8A961] tabular-nums">AI</div>
              <div className="text-xs text-white/40 mt-1">Powered</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
